import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react'
import logo from '@/assets/brand/logo.png'
import { UserAvatarMenu } from '@/platform/session/UserAvatarMenu'

export interface ShellNavigationItem {
  kind?: undefined
  id: string
  path: string
  label: string
}

export interface ShellNavigationGroup {
  kind: 'group'
  id: string
  label: string
  items: ShellNavigationItem[]
}

export type ShellNavigationEntry = ShellNavigationItem | ShellNavigationGroup

interface AppShellProps {
  homePath: string
  navigation: ShellNavigationEntry[]
}

export function AppShell({ homePath, navigation }: AppShellProps) {
  const [isNavigationOpen, setNavigationOpen] = useState(false)
  const location = useLocation()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => Object.fromEntries(
    navigation
      .filter((entry): entry is ShellNavigationGroup => 'kind' in entry && entry.kind === 'group')
      .map((group) => [group.id, group.items.some((item) => location.pathname.startsWith(item.path.replace(/\*$/, '')))]),
  ))
  const closeNavigation = () => setNavigationOpen(false)

  return (
    <div className="shell">
      <header className="shell-header">
        <button className="menu-toggle" type="button" onClick={() => setNavigationOpen(true)} aria-label="Mở menu"><Menu size={20} /></button>
        <Brand homePath={homePath} compact />
        <strong className="shell-title">Quản lý học liệu tiếng Anh</strong>
        <UserAvatarMenu />
      </header>
      {isNavigationOpen && <button className="shell-backdrop" type="button" aria-label="Đóng menu" onClick={closeNavigation} />}
      <aside className={`shell-nav ${isNavigationOpen ? 'open' : ''}`} aria-label="Điều hướng biên soạn">
        <div className="shell-brand-row"><Brand homePath={homePath} /><button className="close-menu" type="button" onClick={closeNavigation} aria-label="Đóng menu"><X size={20} /></button></div>
        <nav className="shell-nav-list">
          {navigation.map((entry) => {
            if ('kind' in entry && entry.kind === 'group') {
              const isExpanded = expandedGroups[entry.id] ?? false
              const Chevron = isExpanded ? ChevronDown : ChevronRight
              return <section className="nav-group" key={entry.id} aria-label={entry.label}>
                <button
                  className="nav-group-toggle"
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedGroups((current) => ({ ...current, [entry.id]: !isExpanded }))}
                >
                  <span>{entry.label}</span><Chevron size={16} aria-hidden="true" />
                </button>
                {isExpanded && <div className="nav-group-items">
                  {entry.items.map(({ id, path, label }) => <NavLink key={id} to={path} onClick={closeNavigation} className={({ isActive }) => `nav-item nav-item-nested ${isActive ? 'active' : ''}`}><span>{label}</span></NavLink>)}
                </div>}
              </section>
            }
            return <NavLink key={entry.id} to={entry.path} onClick={closeNavigation} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}><span>{entry.label}</span></NavLink>
          })}
        </nav>
      </aside>
      <main className="shell-main"><Outlet /></main>
    </div>
  )
}

function Brand({ homePath, compact = false }: { homePath: string; compact?: boolean }) {
  return <NavLink to={homePath} className={`brand ${compact ? 'compact' : ''}`}>
    <img src={logo} alt="Học Nhé" />
    <span><b>Học Nhé</b><small>Học cùng nhau</small></span>
  </NavLink>
}
